import { useNavigate } from "react-router-dom";
import { MapPin, ChevronRight, Sparkles, Loader2, Clock, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { DISCIPLINE_COLORS, relativeDate, scanPass } from "@/lib/format";

const STATUS_META = {
  done: { label: "Intel ready", dot: "bg-emerald-500", text: "text-emerald-400" },
  running: { label: "Scanning…", dot: "bg-[#E8833A] animate-pulse", text: "text-[#E8833A]" },
  queued: { label: "Queued", dot: "bg-[#7FB2E0]", text: "text-[#7FB2E0]" },
  error: { label: "Scan failed", dot: "bg-red-500", text: "text-red-400" },
  idle: { label: "Not scanned", dot: "bg-[#3A3D48]", text: "text-[#686B77]" },
};

export const AthleteCard = ({ athlete }) => {
  const nav = useNavigate();
  const status = STATUS_META[athlete.discovery_status] || STATUS_META.idle;
  const running = athlete.discovery_status === "running";
  const pass = running ? scanPass(athlete.discovery_stage) : null;
  const topStanding = (athlete.standings || [])
    .slice()
    .sort((a, b) => a.rank - b.rank)[0];
  const stageLabel = running && pass ? pass.short : status.label;

  return (
    <button
      data-testid="athlete-card"
      onClick={() => nav(`/athlete/${athlete.id}`)}
      className="text-left w-full rounded-xl border border-[#262933] bg-[#12141A] p-5 hover:border-[#E8833A]/40 hover:-translate-y-1 transition-transform duration-200 group"
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <h3 className="font-display font-bold text-[#F3EFE6] text-lg leading-tight truncate">
            {athlete.known_name}
          </h3>
          <div className="flex items-center gap-1 mt-1 text-[#9A9CA6] text-xs">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate">{athlete.hometown || "—"}</span>
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-[#686B77] group-hover:text-[#E8833A] transition-colors shrink-0" />
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {(athlete.disciplines || []).map((d) => (
          <span
            key={d}
            className="px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-wider font-semibold border"
            style={{
              color: DISCIPLINE_COLORS[d] || "#9A9CA6",
              borderColor: `${DISCIPLINE_COLORS[d] || "#9A9CA6"}44`,
              backgroundColor: `${DISCIPLINE_COLORS[d] || "#9A9CA6"}14`,
            }}
          >
            {d}
          </span>
        ))}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-[#1E212A]">
        {topStanding ? (
          <div className="font-mono text-[11px] text-[#9A9CA6]">
            <span className="text-[#E8833A] font-semibold">#{topStanding.rank}</span> CPRA ·{" "}
            ${topStanding.earnings.toLocaleString()}
          </div>
        ) : (
          <div className="font-mono text-[11px] text-[#686B77]">no standings</div>
        )}
        <div className={cn("flex items-center gap-1.5 text-[11px] font-mono truncate max-w-[55%]", status.text)}>
          {running ? (
            <Loader2 className="h-3 w-3 animate-spin shrink-0" />
          ) : (
            <span className={cn("h-2 w-2 rounded-full shrink-0", status.dot)} />
          )}
          <span className="truncate">
            {running && pass?.n ? `${pass.short} · ${pass.n}/${pass.total}` : stageLabel}
          </span>
        </div>
      </div>

      {running && pass && (
        <div className="mt-3 h-1.5 rounded-full bg-[#0F1116] overflow-hidden" data-testid="card-scan-progress">
          <div className="h-full bg-[#E8833A] transition-all duration-700" style={{ width: `${pass.pct}%` }} />
        </div>
      )}

      {athlete.last_discovery && (
        <div className="flex items-center gap-1 mt-2 font-mono text-[10px] text-[#686B77]">
          <Clock className="h-3 w-3" /> Last scan {relativeDate(athlete.last_discovery)}
        </div>
      )}
    </button>
  );
};

export const RunDiscoveryButton = ({ onClick, status, className, label, cost = true, isAdmin = false }) => {
  const running = status === "running";
  const scanned = status === "done" || status === "error";
  const text = label || (scanned ? "Refresh" : "Run discovery");
  return (
    <button
      data-testid="run-discovery-button"
      onClick={onClick}
      disabled={running}
      className={cn(
        "flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition-colors duration-200 disabled:opacity-70",
        "bg-[#E8833A] text-[#0B0D11] hover:bg-[#F0924A]",
        className
      )}
    >
      {running ? <Loader2 className="h-4 w-4 animate-spin" /> : scanned ? <RefreshCw className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
      {running ? "Gathering intel…" : text}
      {!running && cost && !isAdmin && (
        <span className="text-[11px] font-mono bg-black/15 rounded px-1.5 py-0.5">1 credit</span>
      )}
    </button>
  );
};
